import { Grid, Stack } from "@mui/material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import InfoSubTitle from "../../../common/InfoSubTitle";
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import { IEmailErrors, IInputEmailDistribution, TEmailDistributionUpdateParams } from "@/Settings/types/SystemSettings/emailDistribution";
import { ICITAccountNumbers, ICITScheduledAccountNumbers } from "@/Settings/types/Common/model";
import { useQueryClient } from "@tanstack/react-query";
import SimpleTextField from "@/Layouts/components/InputComponents/SimpleTextField";
import DoubleCheckingButton from "@/Layouts/components/Buttons/DoubleCheckingButton";
import CitSelectMultiple from "./CitSelectMultiple";
import isEqual from "lodash-es/isEqual";
import SimpleDoubleChecking from "@/Layouts/components/Buttons/SimpleDoubleChecking";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import SimpleIconButton from "@/Layouts/components/Buttons/SimpleIconButton";
import AddCircleOutlineRoundedIcon from "@mui/icons-material/AddCircleOutlineRounded";
import AddCircleRounded from "@mui/icons-material/AddCircleRounded";
import getSelectEmails from "@/Settings/helpers/SystemSettings/emailDistribution/getSelectEmails";
import TrashIconWhite from "@/assets/icons/motivators/spiffSetup/trash-white.svg";

const getSelectedEmployees = (
	employees: { employeeId: string }[] = [],
	selected: {
		employeeId: string;
		_id: string;
	}[] = []
) => {
	const mappedSelected = selected.map((item) => item.employeeId);
	return employees?.filter((employee) => mappedSelected.includes(employee.employeeId));
};

const dropdownStyles = { maxWidth: "250px" };

const getInitialValueOptions = (initialValue: string[] = []) => {
	return initialValue.map((value) => ({ label: value, order: 0, hasOptions: false }));
};

const getAccountSourceLabel = (administrationToolsSourceLabel: { label: string; _id: string; value: string }[], id: string) => {
	return administrationToolsSourceLabel?.find((source) => source._id === id)?.label;
};

const resolveLabel = (account: ICITAccountNumbers, administrationToolsSourceLabel: TAdministrationTools) => {
	return account?.sourceLabelId
		? {
				label: getAccountSourceLabel(administrationToolsSourceLabel?.integrations?.dmsSources ?? [], account.sourceLabelId) ?? "",
		  }
		: null;
};

type TEmailDistributionLayoutProps = {
	distribution: IInputEmailDistribution;
	citScheduledAccountNumbers?: ICITScheduledAccountNumbers[];
	errors: IEmailErrors;
	index: number;
	onRemove: (index: number) => void;
	onActionUpdate: ({ index, key, value }: TEmailDistributionUpdateParams) => void;
	locationId: string;
	isPending: boolean;
};

type TAdministrationTools = { integrations: { dmsSources: { label: string; _id: string; value: string }[] } };

const EmailDistributionLayout = (props: TEmailDistributionLayoutProps) => {
	const { distribution, citScheduledAccountNumbers, index, onRemove, onActionUpdate, locationId, isPending } = props;

	const queryClient = useQueryClient();

	const { getEmployees: employeeListData, getAdministrationTools: administrationToolsSourceLabel } = queryClient.getQueryData([
		"systemSettingsAdditionalInfo",
		locationId,
	]) as {
		getEmployees: { employeeId: string }[];
		getAdministrationTools: TAdministrationTools;
	};

	const handleToIndividuals = (selected: { employeeId: string; _id: string }[]) => {
		const updated = [...(distribution?.toIndividuals ?? [])];
		const selectedEmployees = selected.map((employee) => ({ employeeId: employee.employeeId, _id: employee._id }));
		if (!isEqual(selectedEmployees, updated)) {
			onActionUpdate({ index, key: "toIndividuals", value: selectedEmployees });
		}
	};

	const handleEmailValues = (emailValues: string) => {
		const splitEmails: string[] = emailValues?.split(",").map((element) => element.trim());
		onActionUpdate({ index, key: "toNonEmployee", value: splitEmails });
	};

	const handleUpdateSourceAccount = (values: { label: string }[], key: number) => {
		const updated = [...(distribution?.accountNumbers ?? [])];
		updated[key].sourceAccount = values.map((value) => value.label);
		onActionUpdate({ index, key: "accountNumbers", value: updated });
	};

	const handleDestination = (text: string, key: number) => {
		const updated = [...(distribution?.accountNumbers ?? [])];
		updated[key].destinationAccount = text;
		onActionUpdate({ index, key: "accountNumbers", value: updated });
	};

	const handleSourceLabel = (selected: { label: string; _id: string; value: string }, key: number) => {
		const updated = [...(distribution?.accountNumbers ?? [])];
		updated[key].sourceLabelId = selected?._id;
		onActionUpdate({ index, key: "accountNumbers", value: updated });
	};

	const handleRemoveAccount = (key: number) => {
		const updated = [...(distribution?.accountNumbers ?? [])];
		updated.splice(key, 1);
		onActionUpdate({ index, key: "accountNumbers", value: updated });
	};

	const handleAddAccount = () => {
		const updated = [...(distribution?.accountNumbers ?? [])];
		updated.push({
			sourceAccount: [],
			destinationAccount: "",
			sourceLabelId: "",
		});
		onActionUpdate({ index, key: "accountNumbers", value: updated });
	};

	return (
		<Grid container item xs>
			<Grid item container>
				<Grid item xs={3}>
					<InfoSubTitle title="To Individual(s):" className="!mb-[12px]" />
					<SmartSelector
						multiple
						tagsVariant={"text"}
						selectBy={"employeeId"}
						showBy={"fullName"}
						options={employeeListData || []}
						selected={getSelectedEmployees(employeeListData, distribution?.toIndividuals)}
						inputProps={{
							placeholder: "Select an Option",
						}}
						listBoxProps={{
							sx: { maxHeight: "220px" },
						}}
						clearable
						disabled={isPending}
						error={Boolean(props.errors?.toIndividuals)}
						sx={dropdownStyles}
						onChangeAction={(values) => handleToIndividuals(values as { employeeId: string; _id: string }[])}
					/>
				</Grid>
				<Grid item xs={3} ml={1}>
					<InfoSubTitle title="To Non-Employees:" className="!mb-[12px]" />
					<SimpleTextField
						sx={{ maxWidth: "250px" }}
						editable
						outlined
						disabled={isPending}
						skeletonWidth={250}
						text={getSelectEmails(distribution?.toNonEmployee)}
						placeholder={"jhon@gmail.com, d.smith@hotmail.com"}
						error={Boolean(props.errors?.toNonEmployee)}
						onActionUpdate={handleEmailValues}
					/>
				</Grid>
				<Grid item xs={3} mt={-1}>
					<DoubleCheckingButton
						title={"Remove Email distribution"}
						sx={{ marginLeft: "-5px" }}
						endIcon={<img src={TrashIconWhite} className="w-4.5" />}
						confirmationButtonClasses="!w-[190px] hover:!border-gray-blue hover:!bg-gray-blue hover:!shadow-gray-blue"
						mainButtonClasses={"btn-simple-remove btn-pack-remove"}
						mainSx={{ marginLeft: "10px" }}
						confirmationSx={{ right: 0, left: "10px" }}
						onDeleteAction={() => onRemove(index)}
					/>
				</Grid>
				<Grid item xs={12} className="!mt-[24px]">
					<Stack direction="row">
						<InfoSubTitle title="Source Account:" className="!mb-[12px]" />
						<InfoSubTitle title="Destination Account:" className="!ml-[148px] !mb-[12px]" />
						<InfoSubTitle title="Source Label:" className="!ml-[110px] !mb-[12px]" />
					</Stack>
				</Grid>
				{distribution?.accountNumbers?.map((account: ICITAccountNumbers, key: number) => {
					const keyIndex = `cit-Email-Distribution-${index}-account-numbers-${key}`;
					const isNotFirst = key !== 0;
					const classes = isNotFirst ? "!mt-[12px]" : "";
					return (
						<Grid container key={keyIndex} className={classes}>
							<Grid item xs={3}>
								<CitSelectMultiple
									placeholder={"Select Source Account"}
									initialValue={getInitialValueOptions(account.sourceAccount || [])}
									options={citScheduledAccountNumbers}
									className={"advanced-select regular-input-text"}
									disabled={isPending}
									error={!account?.sourceAccount?.length || Boolean(props.errors?.accountNumbers?.[key]?.sourceAccount)}
									onSettled={(values) => handleUpdateSourceAccount(values, key)}
								/>
							</Grid>
							<Grid item xs={3} ml={1}>
								<SimpleTextField
									sx={{ width: "250px" }}
									editable
									outlined
									text={account?.destinationAccount || ""}
									placeholder={"Enter Destination Account"}
									disabled={isPending}
									inputProps={{ maxLength: 5 }}
									error={!account?.destinationAccount || Boolean(props.errors?.accountNumbers?.[key]?.destinationAccount)}
									onActionUpdate={(text: string) => handleDestination(text, key)}
								/>
							</Grid>
							<Grid item xs={4} ml={1}>
								<SmartSelector
									showBy={"label"}
									selectBy={"label"}
									options={administrationToolsSourceLabel?.integrations?.dmsSources ?? []}
									selected={resolveLabel(account, administrationToolsSourceLabel)}
									inputProps={{
										placeholder: "Select Source Label",
									}}
									listBoxProps={{
										sx: { maxHeight: "220px" },
									}}
									error={!account?.sourceLabelId || Boolean(props.errors?.accountNumbers?.[key]?.sourceLabelId)}
									disabled={isPending}
									sx={dropdownStyles}
									onChangeAction={(selected) =>
										handleSourceLabel(selected as { label: string; _id: string; value: string }, key)
									}
								/>
							</Grid>
							<Grid item xs={1} ml={-12} mt={-0.5}>
								<SimpleDoubleChecking
									className={"btn-delete"}
									disabled={Boolean(distribution?.accountNumbers?.length === 1) || isPending}
									color={"var(--gray-blue-color)"}
									confirmationButtonColor={"var(--gray-blue-color)"}
									icon={<CloseRoundedIcon fontSize="small" />}
									iconHover={<CloseRoundedIcon fontSize="small" />}
									confirmationIcon={<DeleteOutlineOutlinedIcon fontSize="small" />}
									onDeleteAction={() => handleRemoveAccount(key)}
								/>
							</Grid>
						</Grid>
					);
				})}
				<Grid item xs={10} className="flex justify-center items-center !mt-[24px]">
					<SimpleIconButton
						className={"btn-add"}
						color={"var(--primary-color)"}
						disabled={false}
						icon={<AddCircleOutlineRoundedIcon fontSize="large" />}
						iconHover={<AddCircleRounded fontSize="large" />}
						onClick={handleAddAccount}
					/>
				</Grid>
			</Grid>
		</Grid>
	);
};

export default EmailDistributionLayout;
